import { useEffect, useState } from "react";

const MESSAGES = [
  "Authenticating session",
  "Loading intelligence matrix",
  "Syncing field data",
  "Initializing map layers",
  "Preparing dashboard",
];

export default function CinematicLoader() {
  const [msgIdx, setMsgIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setMsgIdx((i) => (i + 1) % MESSAGES.length), 2400);
    return () => clearInterval(id);
  }, []);

  return (
    <>
      <style>{`
        @keyframes cl-spin-cw {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes cl-spin-ccw {
          from { transform: rotate(0deg); }
          to   { transform: rotate(-360deg); }
        }
        @keyframes cl-fade-up {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes cl-pulse-glow {
          0%, 100% { opacity: 0.28; transform: scale(1); }
          50%       { opacity: 0.55; transform: scale(1.06); }
        }
        @keyframes cl-progress {
          0%   { left: -38%; }
          60%  { left: 100%; }
          100% { left: 100%; }
        }
        @keyframes cl-dot-pulse {
          0%, 100% { opacity: 0.35; transform: scale(1); }
          50%       { opacity: 1;    transform: scale(1.6); }
        }
        @keyframes cl-msg-in {
          from { opacity: 0; transform: translateY(5px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes cl-blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0; }
        }
        @keyframes cl-ring-pulse {
          0%, 100% { opacity: 0.08; }
          50%       { opacity: 0.18; }
        }
        @keyframes cl-logo-breathe {
          0%, 100% { opacity: 0.85; transform: scale(1); }
          50%       { opacity: 1;    transform: scale(1.04); }
        }
      `}</style>

      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "#030e07",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "var(--font-body, system-ui, sans-serif)",
          overflow: "hidden",
          userSelect: "none",
        }}
      >
        {/* ── Dot-grid background ──────────────────────────────────── */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(circle, rgba(255,255,255,0.055) 1px, transparent 1px)",
            backgroundSize: "36px 36px",
            pointerEvents: "none",
          }}
        />

        {/* ── Central radial glow ──────────────────────────────────── */}
        <div
          style={{
            position: "absolute",
            width: 560,
            height: 560,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(16,78,42,0.55) 0%, transparent 68%)",
            animation: "cl-pulse-glow 3.5s ease-in-out infinite",
            pointerEvents: "none",
          }}
        />

        {/* ── Static concentric rings ─────────────────────────────── */}
        {[200, 320, 440].map((d, i) => (
          <div
            key={d}
            style={{
              position: "absolute",
              width: d,
              height: d,
              borderRadius: "50%",
              border: "1px solid rgba(74,222,128,0.06)",
              animation: `cl-ring-pulse ${3 + i * 0.8}s ease-in-out ${i * 0.4}s infinite`,
              pointerEvents: "none",
            }}
          />
        ))}

        {/* ── Main content block ───────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            zIndex: 2,
            animation: "cl-fade-up 0.9s cubic-bezier(.22,1,.36,1) both",
          }}
        >
          {/* Logo + spinner rings */}
          <div
            style={{
              position: "relative",
              width: 108,
              height: 108,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 32,
            }}
          >
            {/* Outer spinning arc */}
            <svg
              style={{
                position: "absolute",
                inset: -8,
                width: 124,
                height: 124,
                animation: "cl-spin-cw 3.2s linear infinite",
              }}
              viewBox="0 0 124 124"
            >
              <circle
                cx="62" cy="62" r="57"
                fill="none"
                stroke="rgba(74,222,128,0.55)"
                strokeWidth="1.5"
                strokeDasharray="90 268"
                strokeLinecap="round"
              />
            </svg>

            {/* Inner counter-spinning arc */}
            <svg
              style={{
                position: "absolute",
                inset: 10,
                width: 88,
                height: 88,
                animation: "cl-spin-ccw 2.1s linear infinite",
              }}
              viewBox="0 0 88 88"
            >
              <circle
                cx="44" cy="44" r="38"
                fill="none"
                stroke="rgba(74,222,128,0.28)"
                strokeWidth="1"
                strokeDasharray="44 195"
                strokeLinecap="round"
              />
            </svg>

            {/* Static halo ring */}
            <div
              style={{
                position: "absolute",
                inset: 4,
                borderRadius: "50%",
                border: "1px solid rgba(74,222,128,0.1)",
              }}
            />

            {/* Logo */}
            <img
              src="/ICON2.png"
              alt="WITS"
              style={{
                width: 48,
                height: 48,
                objectFit: "contain",
                position: "relative",
                zIndex: 1,
                animation: "cl-logo-breathe 3s ease-in-out infinite",
              }}
            />
          </div>

          {/* Wordmark */}
          <p
            style={{
              color: "rgba(255,255,255,0.92)",
              fontSize: 28,
              fontWeight: 800,
              letterSpacing: "0.24em",
              fontFamily: "var(--font-display, system-ui, sans-serif)",
              margin: 0,
              lineHeight: 1,
            }}
          >
            WITS
          </p>

          {/* Sub-label */}
          <p
            style={{
              color: "rgba(74,222,128,0.5)",
              fontSize: 8.5,
              letterSpacing: "0.3em",
              textTransform: "uppercase",
              margin: "8px 0 36px",
              lineHeight: 1,
            }}
          >
            Wildlife Intelligence &amp; Tracking System
          </p>

          {/* Status line */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              height: 18,
            }}
          >
            {/* Pulsing dot */}
            <div
              style={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: "rgba(74,222,128,0.85)",
                flexShrink: 0,
                animation: "cl-dot-pulse 1.4s ease-in-out infinite",
              }}
            />

            {/* Cycling message */}
            <p
              key={msgIdx}
              style={{
                color: "rgba(74,222,128,0.6)",
                fontSize: 10,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                margin: 0,
                animation: "cl-msg-in 0.35s ease-out both",
                whiteSpace: "nowrap",
              }}
            >
              {MESSAGES[msgIdx]}
            </p>

            {/* Cursor */}
            <span
              style={{
                color: "rgba(74,222,128,0.75)",
                fontWeight: 700,
                fontSize: 12,
                animation: "cl-blink 1s step-end infinite",
                lineHeight: 1,
              }}
            >
              _
            </span>
          </div>

          {/* Progress track */}
          <div
            style={{
              width: 200,
              height: 1.5,
              background: "rgba(255,255,255,0.06)",
              borderRadius: 9999,
              marginTop: 20,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                height: "100%",
                width: "38%",
                background:
                  "linear-gradient(90deg, transparent, rgba(74,222,128,0.9), rgba(134,239,172,0.6), transparent)",
                borderRadius: 9999,
                animation: "cl-progress 2.2s cubic-bezier(.4,0,.2,1) infinite",
              }}
            />
          </div>
        </div>

        {/* ── Corner metadata (intelligence aesthetic) ─────────────── */}
        <div
          style={{
            position: "absolute",
            bottom: 22,
            left: 26,
            color: "rgba(74,222,128,0.18)",
            fontSize: 8.5,
            letterSpacing: "0.08em",
            fontFamily: "monospace",
          }}
        >
          0&deg;02&prime;21&Prime;S&nbsp;&nbsp;37&deg;54&prime;22&Prime;E
        </div>
        <div
          style={{
            position: "absolute",
            bottom: 22,
            right: 26,
            color: "rgba(74,222,128,0.18)",
            fontSize: 8.5,
            letterSpacing: "0.1em",
            fontFamily: "monospace",
          }}
        >
          SYS&nbsp;&middot;&nbsp;INIT
        </div>

        {/* ── Top-right version tag ───────────────────────────────── */}
        <div
          style={{
            position: "absolute",
            top: 22,
            right: 26,
            color: "rgba(255,255,255,0.1)",
            fontSize: 8.5,
            letterSpacing: "0.1em",
            fontFamily: "monospace",
          }}
        >
          v1.0.0
        </div>
      </div>
    </>
  );
}
